/**
 * Formats a cent amount using a Shopify money_format string (e.g. "${{amount}}").
 * Mirrors the subset of Shopify.formatMoney used across storefronts.
 * @param {number} cents - Amount in cents.
 * @param {string} format - Shopify shop.money_format string.
 * @returns {string} Formatted money string.
 */
function bulkOrderGridFormatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const [dollars, decimals] = value.split('.');
  const dollarsWithCommas = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const placeholders = {
    amount: `${dollarsWithCommas}.${decimals}`,
    amount_no_decimals: dollarsWithCommas,
    amount_with_comma_separator: `${dollars.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`,
    amount_no_decimals_with_comma_separator: dollars.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  };

  return (format || '${{amount}}').replace(/{{\s*(\w+)\s*}}/g, (match, key) => (
    Object.prototype.hasOwnProperty.call(placeholders, key) ? placeholders[key] : match
  ));
}

class BulkOrderGrid extends HTMLElement {
  connectedCallback() {
    this.cartAddUrl = this.dataset.cartAddUrl;
    this.moneyFormat = this.dataset.moneyFormat;
    this.noQuantityError = this.dataset.noQuantityError;
    this.addingText = this.dataset.addingText;
    this.qtyInputs = Array.from(this.querySelectorAll('.bulk-order-grid__qty'));
    this.totalQtyEl = this.querySelector('[data-bulk-total-qty]');
    this.totalPriceEl = this.querySelector('[data-bulk-total-price]');
    this.addAllBtn = this.querySelector('[data-bulk-add-all]');
    this.errorEl = this.querySelector('.bulk-order-grid__error');

    this.qtyInputs.forEach((input) => {
      input.addEventListener('input', this.handleQtyChange.bind(this));
    });

    if (this.addAllBtn) {
      this.addAllBtn.addEventListener('click', this.handleAddAll.bind(this));
    }

    // Mobile per-colour view: track which colour row is "current" so only
    // that colour's sizes are shown (see .bulk-order-grid__row[data-current]).
    this.sectionId = this.dataset.sectionId;
    this.colorOptionPosition = parseInt(this.dataset.colorOptionPosition, 10) || 0;
    this.colorRows = Array.from(this.querySelectorAll('.bulk-order-grid__row[data-color-value]'));

    if (this.colorOptionPosition > 0 && this.colorRows.length > 0) {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      document.addEventListener('on:variant:change', this.boundHandleVariantChange);
    }

    this.updateTotals();
  }

  disconnectedCallback() {
    if (this.boundHandleVariantChange) {
      document.removeEventListener('on:variant:change', this.boundHandleVariantChange);
    }
  }

  /**
   * Keeps the mobile per-colour view in sync with the product's variant-picker.
   * @param {CustomEvent} evt - 'on:variant:change' event dispatched by <variant-picker>.
   */
  handleVariantChange(evt) {
    if (evt.target.dataset.sectionId !== this.sectionId) return;

    const colorValue = evt.detail.selectedOptions[this.colorOptionPosition - 1];
    if (!colorValue) return;

    this.colorRows.forEach((row) => {
      row.toggleAttribute('data-current', row.dataset.colorValue === colorValue);
    });
  }

  handleQtyChange(evt) {
    const input = evt.target;
    const value = parseInt(input.value, 10);
    if (Number.isNaN(value) || value < 0) {
      input.value = '';
    }
    this.updateTotals();
  }

  getLineItems() {
    return this.qtyInputs
      .map((input) => ({
        id: parseInt(input.dataset.variantId, 10),
        quantity: parseInt(input.value, 10) || 0,
        price: parseFloat(input.dataset.price) || 0
      }))
      .filter((item) => item.quantity > 0);
  }

  updateTotals() {
    const items = this.getLineItems();
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = items.reduce((sum, item) => sum + (item.quantity * item.price), 0);

    if (this.totalQtyEl) this.totalQtyEl.textContent = totalQty;
    if (this.totalPriceEl) this.totalPriceEl.textContent = bulkOrderGridFormatMoney(totalPrice, this.moneyFormat);
    if (this.addAllBtn) this.addAllBtn.disabled = totalQty === 0;

    this.setError(false);
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.hidden = !message;
    this.errorEl.textContent = message || '';
  }

  async handleAddAll() {
    const items = this.getLineItems();
    if (items.length === 0) {
      this.setError(this.noQuantityError);
      return;
    }

    const originalBtnText = this.addAllBtn.textContent;
    this.addAllBtn.disabled = true;
    this.addAllBtn.classList.add('is-loading');
    if (this.addingText) this.addAllBtn.textContent = this.addingText;
    this.setError(false);

    try {
      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: items.map((item) => ({ id: item.id, quantity: item.quantity }))
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const message = typeof data.description === 'string' ? data.description : this.noQuantityError;
        throw new Error(message);
      }

      this.qtyInputs.forEach((input) => { input.value = ''; });
      this.updateTotals();

      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { items } }));

      if (theme.settings.afterAddToCart === 'drawer') {
        const drawer = document.querySelector('.js-cart-drawer');
        if (drawer) drawer.open(this.addAllBtn);
      } else if (theme.settings.afterAddToCart === 'page') {
        window.location.href = theme.routes.cart;
      }
    } catch (error) {
      this.setError(error.message);
    } finally {
      this.addAllBtn.classList.remove('is-loading');
      this.addAllBtn.textContent = originalBtnText;
      this.addAllBtn.disabled = this.getLineItems().length === 0;
    }
  }
}

window.customElements.define('bulk-order-grid', BulkOrderGrid);
